// src/hooks/useChatManager.js
import { useState, useCallback } from 'react';
import { chatbotApi } from '../Services/chatbotApi';

export const useChatManager = (showToast, clearSessionCallback) => {
    const [conversations, setConversations] = useState([]);
    const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activeConversationId, setActiveConversationId] = useState(null);

    const generateConversationId = useCallback(() => {
        return 'conv-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
    }, []);

    const loadConversations = useCallback(async (userUuid) => {
        if (!userUuid) return;
        try {
            const responseData = await chatbotApi.getConversations(userUuid);
            if (responseData.success && responseData.conversations) {
                setConversations(responseData.conversations);
            } else {
                setConversations([]);
            }
        } catch (error) {
            console.error('Error loading conversations:', error);
            showToast('Error al cargar conversaciones', 'error');
        }
    }, [showToast]);

    const loadConversationMessages = useCallback(async (userUuid, conversation) => {
        if (!userUuid || !conversation.conversation_id) {
            setMessages([
                { sender: 'user', content: conversation.message },
                { sender: 'bot', content: conversation.response }
            ]);
            return;
        }

        //  if (activeConversationId === conversation.conversation_id && messages.length > 0) {            
        //     return;
        // }

        setIsLoading(true);
        setActiveConversationId(conversation.conversation_id);
        setMessages([]);

        try {
            const responseData = await chatbotApi.getConversationMessages(userUuid, conversation.conversation_id);
            if (responseData.success && responseData.messages) {
                const loadedMessages = responseData.messages.flatMap(msg => [
                    { sender: 'user', content: msg.message, timestamp: msg.timestamp, messageId: msg.message_id, agentUsed: msg.agent_used },
                    { sender: 'bot', content: msg.response, messageId: msg.message_id, agentUsed: msg.agent_used }
                ]);
                setMessages(loadedMessages);
            }
        } catch (error) {
            console.error('Error cargando mensajes:', error);
            showToast('Error al cargar el historial', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [showToast]);

    const deleteConversation = useCallback(async (userUuid, timestamp, conversation_id) => {
        if (!userUuid || !timestamp) {
            showToast('Invalid parameters for delete', 'error');
            return { wasActive: false };
        }

        try {
            const conversation = conversations.find(c => c.timestamp === timestamp);
            const conversationId = conversation?.conversation_id;
            const wasActiveConversation = activeConversationId === conversationId;

            await chatbotApi.deleteConversation(userUuid, timestamp, conversationId);

            setConversations(prev => prev.filter(conv => conv.timestamp !== timestamp));

            if (conversationId && clearSessionCallback) {
                clearSessionCallback(conversationId);
            }

            if (wasActiveConversation) {
                setActiveConversationId(null);
                setMessages([]);
            }

            showToast('Conversation deleted', 'success');

            // Retornar si era la conversación activa
            return { wasActive: wasActiveConversation, conversationId };

        } catch (error) {
            console.error('Error deleting conversation:', error);
            showToast('Error deleting conversation', 'error');
            return { wasActive: false };
        }
    }, [conversations, activeConversationId, showToast, clearSessionCallback]);

    const saveUserMessage = useCallback(async ({
        messageId,
        conversationId,
        userUuid,
        userEmail,
        userMessage,
        projectId,
        subscriptionPlan,
        subscriptionStatus
    }) => {
        try {
            const response = await chatbotApi.createMessageRecord({
                messageId,
                conversationId,
                userUuid,
                userEmail,
                userMessage,
                projectId,
                subscriptionPlan,
                subscriptionStatus
            });

            if (!response.success) {
                console.error('Error saving user message:', response);
            }

            return response.success;
        } catch (error) {
            console.error('Error in saveUserMessage:', error);
            return false;
        }
    }, []);

    const saveBotResponse = useCallback(async ({
        messageId,
        botResponse,
        agentUsed,
        responseTimeMs,
        input_tokens,
        output_tokens
    }) => {
        try {
            const response = await chatbotApi.updateBotResponse({
                messageId,
                botResponse,
                agentUsed,
                responseTimeMs,
                input_tokens,
                output_tokens
            });

            if (!response.success) {
                console.error('Error saving bot response:', response);
            }

            return response.success;
        } catch (error) {
            console.error('Error in saveBotResponse:', error);
            return false;
        }
    }, []);

    return {
        conversations,
        messages,
        setMessages,
        isLoading,
        setIsLoading,
        activeConversationId,
        setActiveConversationId,
        generateConversationId,
        loadConversations,
        loadConversationMessages,
        deleteConversation,
        saveUserMessage,
        saveBotResponse
    };
};